import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Download, Search, FileText } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Catalogue sources — reuse the same viewer for both İKEL MT and Guibang.
export const SOURCES = {
  ikel: {
    basePath: "/catalogue/pages",
    thumbBasePath: "/catalogue/thumbs",
    textIndex: "/catalogue/pages-text.json",
    pdfUrl: "/catalogue/ikel-mt-catalogue.pdf",
    totalPages: 137,
    logoSrc: "/brand/ikel-mt-logo.png",
    logoAlt: "İKEL MT",
    accent: "#F59E0B",
  },
  guibang: {
    basePath: "/catalogue/guibang/pages",
    thumbBasePath: "/catalogue/guibang/thumbs",
    textIndex: "/catalogue/guibang/pages-text.json",
    pdfUrl: null,
    totalPages: 58,
    logoSrc: "/brand/guibang-logo.png",
    logoAlt: "Guibang",
    accent: "#1D4ED8",
  },
};

const pad = (n) => String(n).padStart(3, "0");

// Keep back-compat exports used by IkelMt.jsx preview tiles
const TOTAL_PAGES = SOURCES.ikel.totalPages;
const pageUrl = (n) => `${SOURCES.ikel.basePath}/p-${pad(n)}.jpg`;
const thumbUrl = (n) => `${SOURCES.ikel.thumbBasePath}/p-${pad(n)}.jpg`;
const PDF_URL = SOURCES.ikel.pdfUrl;

export default function CatalogueViewer({ open, onClose, initialPage = 1, source = SOURCES.ikel }) {
  const { t } = useLang();
  const [current, setCurrent] = useState(initialPage);
  const [pagesText, setPagesText] = useState(null);
  const [query, setQuery] = useState("");
  const thumbStripRef = useRef(null);

  const {
    basePath, thumbBasePath, textIndex, pdfUrl,
    totalPages, logoSrc, logoAlt, accent,
  } = source;

  const pageUrlFor = (n) => `${basePath}/p-${pad(n)}.jpg`;
  const thumbUrlFor = (n) => `${thumbBasePath}/p-${pad(n)}.jpg`;

  useEffect(() => {
    if (open) setCurrent(initialPage);
  }, [open, initialPage]);

  // Reset search when source changes
  useEffect(() => {
    setQuery("");
    setPagesText(null);
  }, [source]);

  // Lazy-load the pages-text index only when the viewer opens
  useEffect(() => {
    if (!open || pagesText) return;
    fetch(textIndex)
      .then((r) => (r.ok ? r.json() : {}))
      .then(setPagesText)
      .catch(() => setPagesText({}));
  }, [open, pagesText, textIndex]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !pagesText) return null;
    const result = [];
    for (let i = 1; i <= totalPages; i++) {
      const text = (pagesText[String(i)] || "").toLowerCase();
      if (text.includes(q)) result.push(i);
    }
    return result;
  }, [query, pagesText, totalPages]);

  // Auto-jump to first match when a new query produces results
  useEffect(() => {
    if (matches && matches.length > 0) {
      setCurrent(matches[0]);
    }
  }, [matches]);

  const prev = useCallback(
    () => setCurrent((p) => Math.max(1, p - 1)),
    []
  );
  const next = useCallback(
    () => setCurrent((p) => Math.min(totalPages, p + 1)),
    [totalPages]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      // Don't hijack arrows while typing in the search box
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, prev, next]);

  // Scroll current thumb into view in the sidebar
  useEffect(() => {
    if (!open || !thumbStripRef.current) return;
    const el = thumbStripRef.current.querySelector(`[data-thumb="${current}"]`);
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [current, open]);

  if (!open) return null;

  const visiblePages = matches ?? Array.from({ length: totalPages }, (_, i) => i + 1);
  const searchActive = matches !== null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0B1B29]/95 backdrop-blur-sm flex flex-col"
      data-testid="catalogue-viewer"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center gap-3 md:gap-4 px-4 md:px-6 h-16 border-b border-white/10 text-white">
        <div className="flex items-center gap-3 shrink-0">
          <div className="bg-white p-1.5 flex items-center justify-center" style={{ minHeight: 36 }}>
            <img src={logoSrc} alt={logoAlt} className="h-6 w-auto" />
          </div>
          <span className="hidden md:inline text-xs font-mono tracking-[0.2em] uppercase text-white/70">
            {logoAlt} · {t.common.catalogue.eyebrow}
          </span>
        </div>

        {/* Search */}
        <div className="flex-1 flex items-center gap-2 max-w-xl mx-auto">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.common.catalogueSearch.placeholder}
              className="w-full bg-white/10 border border-white/20 focus:border-[#F59E0B] text-white placeholder-white/50 text-sm px-9 py-2 outline-none transition-colors"
              data-testid="catalogue-search-input"
              aria-label={t.common.catalogueSearch.placeholder}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1"
                aria-label={t.common.catalogueSearch.clear}
                data-testid="catalogue-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {searchActive && (
            <span
              className="hidden sm:inline-block text-[10px] font-mono tracking-wider uppercase text-white/60 whitespace-nowrap"
              data-testid="catalogue-search-count"
            >
              {matches.length} {t.common.catalogueSearch.results}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to={`/quote?page=${current}&from=catalogue&brand=${encodeURIComponent(logoAlt)}`}
            className="hidden sm:inline-flex items-center gap-2 text-[#0B1B29] text-xs font-semibold tracking-wider uppercase px-4 py-2 transition-colors hover:bg-white"
            style={{ backgroundColor: accent }}
            data-testid="catalogue-quote-cta"
          >
            <FileText size={14} /> {t.common.requestQuote}
          </Link>
          {pdfUrl && (
            <a
              href={pdfUrl}
              download
              className="hidden sm:inline-flex items-center gap-2 border border-white/20 hover:bg-white hover:text-[#0B1B29] text-xs font-semibold tracking-wider uppercase px-4 py-2 transition-colors"
              data-testid="catalogue-download-top"
            >
              <Download size={14} /> {t.common.catalogue.download}
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t.common.catalogue.close}
            data-testid="catalogue-close"
            className="border border-white/20 hover:bg-white hover:text-[#0B1B29] w-9 h-9 flex items-center justify-center transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Large page */}
        <div className="relative flex-1 flex items-center justify-center p-4 md:p-8">
          {searchActive && matches.length === 0 ? (
            <div className="text-white/75 text-center" data-testid="catalogue-no-results">
              <Search size={48} className="text-white/30 mx-auto mb-4" />
              <p className="text-lg">{t.common.catalogueSearch.noResults}</p>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={prev}
                disabled={current === 1}
                aria-label={t.common.catalogue.prev}
                data-testid="catalogue-prev"
                className="absolute left-3 md:left-6 z-10 w-11 h-11 border border-white/25 bg-[#0B1B29]/60 text-white hover:bg-[#F59E0B] hover:border-[#F59E0B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <ChevronLeft size={20} />
              </button>

              <img
                src={pageUrlFor(current)}
                alt={`${logoAlt} catalogue page ${current}`}
                loading="eager"
                className="max-h-full max-w-full object-contain shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
                data-testid="catalogue-current-page"
              />

              <button
                type="button"
                onClick={next}
                disabled={current === totalPages}
                aria-label={t.common.catalogue.next}
                data-testid="catalogue-next"
                className="absolute right-3 md:right-6 z-10 w-11 h-11 border border-white/25 bg-[#0B1B29]/60 text-white hover:bg-[#F59E0B] hover:border-[#F59E0B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <ChevronRight size={20} />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                <div className="bg-[#0B1B29]/80 border border-white/15 text-white text-xs font-mono tracking-wider uppercase px-4 py-2">
                  {t.common.catalogue.page} {current} {t.common.catalogue.of} {totalPages}
                </div>
                <Link
                  to={`/quote?page=${current}&from=catalogue&brand=${encodeURIComponent(logoAlt)}`}
                  className="sm:hidden text-[#0B1B29] text-xs font-semibold tracking-wider uppercase px-4 py-2"
                  style={{ backgroundColor: accent }}
                  data-testid="catalogue-quote-cta-mobile"
                >
                  {t.common.requestQuote}
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Thumbnail sidebar */}
        <aside
          ref={thumbStripRef}
          className="hidden lg:block w-[220px] border-l border-white/10 overflow-y-auto bg-[#0B1B29]/60"
          data-testid="catalogue-thumb-strip"
        >
          <div className="p-3 grid grid-cols-2 gap-2">
            {visiblePages.map((n) => (
              <button
                key={n}
                type="button"
                data-thumb={n}
                onClick={() => setCurrent(n)}
                className={`relative border transition-all ${
                  n === current
                    ? "border-[#F59E0B]"
                    : "border-white/10 hover:border-white/40 opacity-70 hover:opacity-100"
                }`}
                data-testid={`catalogue-thumb-${n}`}
                aria-label={`Go to page ${n}`}
              >
                <img
                  src={thumbUrlFor(n)}
                  alt=""
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <span className="absolute bottom-0 right-0 bg-[#0B1B29]/80 text-white text-[9px] font-mono px-1.5 py-0.5">
                  {n}
                </span>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

export { TOTAL_PAGES, pageUrl, thumbUrl, PDF_URL };
